import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { Router } from '@angular/router';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';

import { QuickEvaluationContext, QuickEvaluationsAnalytics } from '@recruitee/analytics';
import { QuickEvalOperationType, QuickEvaluationNotificationService } from '@recruitee/candidate';
import type { FullCandidate } from '@recruitee/candidate-types';
import { DevFlag, injectDevFlag } from '@recruitee/dev-flags-data';
import { TranslatePipe } from '@recruitee/i18n';
import {
  InterviewResultsActions,
  isPlacementRatingVisible,
} from '@recruitee/interview-results-data';
import type { InterviewResult } from '@recruitee/interview-results-types';
import { OfferKind } from '@recruitee/offers-types';
import { OfferSelectOption, OfferSelectComponent } from '@recruitee/offers-ui';
import type { Placement } from '@recruitee/placements-types';
import {
  ConnectedOverlayDirective,
  DropdownWidth,
  DropdownComponent,
  ButtonComponent,
  DropdownHeaderComponent,
} from '@recruitee/user-interface';
import { generateUuid } from '@recruitee/utils';

import { InterviewButtonSelectComponent } from '../interview-button-select/interview-button-select.component';
import { InterviewKind } from '../interview-kind';
import { QuickEvaluationContextService } from '../quick-evaluation-context.service';

@Component({
  selector: 'rt-add-quick-evaluation-dropdown',
  templateUrl: 'add-quick-evaluation-dropdown.component.html',
  styleUrls: ['./add-quick-evaluation-dropdown.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [QuickEvaluationsAnalytics],
  standalone: true,
  imports: [
    DropdownComponent,
    ReactiveFormsModule,
    DropdownHeaderComponent,
    NgTemplateOutlet,
    InterviewButtonSelectComponent,
    ButtonComponent,
    OfferSelectComponent,
    TranslatePipe,
  ],
})
export class AddQuickEvaluationDropdownComponent {
  @Input() public dropdownRef: ConnectedOverlayDirective;
  @Input() public candidate: FullCandidate;
  @Input() public placement?: Placement;
  @Input() public isActionToolbar: boolean;
  @Input() public hasAccessToAllJobOffers: boolean;
  @Input() public quickEvaluationTrigger: QuickEvaluationContext;
  @Input() public availablePlacements: Placement[] = [];

  @Output() public optionsClick: EventEmitter<{
    expandQuickEvaluationForm: boolean;
    selectedOffer?: number;
  }> = new EventEmitter();
  @Output() public closeDialog: EventEmitter<void> = new EventEmitter();

  public quickEvaluationForm: UntypedFormGroup;
  public offers: OfferSelectOption[] = [];

  public DropdownWidth = DropdownWidth;

  private hasNewTalentPools = injectDevFlag(DevFlag.NewTalentPools);
  private ngOnDestroy$: Subject<void> = new Subject();

  constructor(
    private formBuilder: UntypedFormBuilder,
    private quickEvaluationNotificationService: QuickEvaluationNotificationService,
    private router: Router,
    private quickEvaluationsAnalytics: QuickEvaluationsAnalytics,
    private quickEvaluationContext: QuickEvaluationContextService,
    private store: Store,
    private actions$: Actions,
  ) {}

  public ngOnInit(): void {
    this.quickEvaluationContext
      .findPlacement(this.availablePlacements)
      .pipe(map(placement => placement || this.placement))
      .subscribe(placement => {
        this.initEvaluationForm(placement);
      });

    this.quickEvaluationForm
      .get('rating')!
      .valueChanges.pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(rating => {
        this.createInterviewResult({ ...this.quickEvaluationForm.value, rating });
        if (!this.isActionToolbar) this.optionsClick.emit({ expandQuickEvaluationForm: false });
        this.close();
      });

    this.quickEvaluationForm
      .get('offerId')!
      .valueChanges.pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(offerId => {
        const stageId = this.candidate.placements.find(placement => offerId === placement.offerId)
          ?.stageId;
        this.quickEvaluationForm.get('stageId')!.setValue(stageId || null);
      });
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public createInterviewResult(value: Partial<InterviewResult>): void {
    let offerId: number | null;
    let talentPoolId: number | null;
    const offer = this.offers.find(offer => offer.id === value.offerId);

    if (this.hasNewTalentPools) {
      offerId =
        ((offer?.kind === OfferKind.Job || this.placement?.offerId) && value.offerId) || null;
      talentPoolId =
        ((offer?.kind === OfferKind.TalentPool || this.placement?.talentPoolId) && value.offerId) ||
        null;
    } else {
      offerId = value.offerId || null;
      talentPoolId = null;
    }

    const data = {
      ...value,
      offerId,
      talentPoolId,
      kind: InterviewKind.QUICK,
    };

    this.showSuccessNotification();

    this.store.dispatch(
      InterviewResultsActions.createOnePessimistic({
        payload: {
          data: {
            value: data,
            candidateId: this.candidate.id,
          },
          correlationId: generateUuid(),
        },
      }),
    );
  }

  public handleClick(expandQuickEvaluationForm: boolean): void {
    if (this.isActionToolbar) {
      this.router.navigate([], {
        queryParams: {
          candidate: this.candidate.id,
          tab: 'interviews',
          selectedOffer: this.quickEvaluationForm.get('offerId')?.value,
        },
        queryParamsHandling: 'merge',
      });
    } else {
      this.optionsClick.emit({
        expandQuickEvaluationForm,
        selectedOffer: this.quickEvaluationForm.get('offerId')?.value,
      });
    }
    this.close();
  }

  public close(): void {
    this.closeDialog.emit();
    this.dropdownRef.open = false;
  }

  private initEvaluationForm(placement?: Placement): void {
    this.quickEvaluationForm = this.formBuilder.group({
      offerId: placement?.offerId || placement?.talentPoolId,
      stageId: placement?.stageId,
      rating: ['', Validators.required],
      ratingNote: '',
    });
    this.offers = this.availablePlacements.length ? this.filterAndMapOffers() : [];
  }

  private filterAndMapOffers(): OfferSelectOption[] {
    return <OfferSelectOption[]>this.availablePlacements.map(placement => ({
      id: placement && (placement.offer?.id || placement.talentPool?.id)!,
      title: placement && (placement.offer?.title || placement.talentPool?.title)!,
      kind: placement && (placement.offer?.kind || placement.talentPool?.kind)!,
      status: placement && (placement.offer?.status || placement.talentPool?.status)!,
    }));
  }

  private showSuccessNotification(): void {
    this.actions$
      .pipe(ofType(InterviewResultsActions.createOnePessimisticSuccess))
      .pipe(take(1))
      .subscribe(data => {
        const hasPlacementsInCandidate = 'placements' in this.candidate;
        const placementRatingVisible = isPlacementRatingVisible(
          hasPlacementsInCandidate ? this.candidate.placements ?? [] : [this.placement!],
          data.payload.data.offerId,
        );
        this.quickEvaluationsAnalytics.trackQuickEvaluate(
          this.quickEvaluationTrigger,
          !placementRatingVisible,
        );
        this.quickEvaluationNotificationService.success({
          operationType: QuickEvalOperationType.ADD,
          rating: data.payload.data?.rating,
        });
      });
  }
}
